import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AmendInvoiceModal } from '@/components/InvoiceDetailsModal/AmendInvoiceModal';
import {
  PaymentConfirmationData,
  PaymentConfirmationForm,
} from '@/components/PaymentConfirmationForm';
import { ApiError } from '@/lib/api';
import {
  type InvoiceDetails,
  type PaymentMovement,
  refetchInvoiceDetails,
  useCreateOpportunity,
  useDownloadInvoicePDF,
  useRecordMovement,
  useSettleInvoice,
  useUpdatePaymentConfirmation,
} from '@/models/invoices';
import { TInvoice } from '@/types';
import { PaymentHistory } from './PaymentHistory';

export type InvoiceActionHandlers = {
  onDownloadPDF: (invoice: TInvoice) => void;
  onProvidePaymentConfirmation: (invoice: TInvoice) => void;
  onCancelInvoice: (invoice: TInvoice) => void;
  onRecordMovement: (invoice: TInvoice) => void;
  onSettle: (invoice: TInvoice) => void;
  onMondayOpportunityCreate: (invoice: TInvoice) => void;
  onAmend: (invoice: TInvoice) => void;
};

/** How long the accountant has to take a settlement back before it is sent. */
const UNDO_SECONDS = 10;

type Snapshot = {
  paidAmount: number;
  paymentSeq: number;
  payments: PaymentMovement[];
};

const snapshotOf = (invoice: InvoiceDetails): Snapshot => ({
  paidAmount: invoice.paidAmount ?? 0,
  paymentSeq: invoice.paymentSeq ?? 0,
  payments: invoice.payments ?? [],
});

// The accountant's local calendar day - toISOString() would flip to the UTC date, which
// around midnight is a different day than the one on their screen.
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
};

// Bundles the invoice mutations + their sub-modals so the details surface (drawer or
// standalone page) can offer full actions.
export function useInvoiceActions(): {
  handlers: InvoiceActionHandlers;
  modals: ReactNode;
} {
  const downloadPDFMutation = useDownloadInvoicePDF();
  const recordMovement = useRecordMovement();
  const settleInvoice = useSettleInvoice();
  const paymentConfirmationMutation = useUpdatePaymentConfirmation();
  const createOpportunity = useCreateOpportunity();

  const [target, setTarget] = useState<TInvoice | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);

  const [movementOpen, setMovementOpen] = useState(false);
  const [direction, setDirection] = useState<'collection' | 'deduction'>(
    'collection',
  );
  const [amountValue, setAmountValue] = useState<number | string>('');
  const [collectedDate, setCollectedDate] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<{
    title: string;
    message: string;
    tone: 'red' | 'orange';
  } | null>(null);

  const [settleOpen, setSettleOpen] = useState(false);
  // Only the countdown holds an unsent command, and it holds it in memory alone. Nothing is
  // persisted: an entry that dies with the tab was simply never made, which is the correct
  // reading of a settlement nobody finished making.
  const [remaining, setRemaining] = useState<number | null>(null);
  const timer = useRef<number | null>(null);

  // Stable, so the listener effect below can depend on it without re-subscribing every tick -
  // and so unmount genuinely clears the interval rather than a stale copy of it.
  const stopCountdown = useCallback(() => {
    if (timer.current != null) clearInterval(timer.current);
    timer.current = null;
    setRemaining(null);
  }, []);

  useEffect(() => stopCountdown, [stopCountdown]);

  // Leaving the screen, hiding the tab or losing the connection all cancel an unsent
  // settlement. It is never sent through unload or a beacon.
  useEffect(() => {
    if (remaining == null) return;
    const cancel = () => {
      stopCountdown();
      setSettleOpen(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') cancel();
    };
    document.addEventListener('visibilitychange', onVisibility);
    globalThis.addEventListener('pagehide', cancel);
    globalThis.addEventListener('offline', cancel);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      globalThis.removeEventListener('pagehide', cancel);
      globalThis.removeEventListener('offline', cancel);
    };
  }, [remaining, stopCountdown]);

  // Cached list data is enough to open a modal, never enough to write against: the movement
  // count decides whether an entry is accepted, and a stale one is refused.
  const openWithFreshInvoice = async (invoice: TInvoice, open: () => void) => {
    setTarget(invoice);
    setSnapshot(null);
    setNotice(null);
    open();
    try {
      const fresh = await refetchInvoiceDetails(invoice.id.toString());
      setTarget(fresh);
      setSnapshot(snapshotOf(fresh));
    } catch {
      setNotice({
        title: 'Could not load this invoice',
        message:
          'Nothing can be recorded until its current figures are known. Close this and try again.',
        tone: 'orange',
      });
    }
  };

  const handlers: InvoiceActionHandlers = {
    onDownloadPDF: (invoice) =>
      downloadPDFMutation.mutate(invoice.id.toString()),
    onProvidePaymentConfirmation: (invoice) => {
      setTarget(invoice);
      setPaymentOpen(true);
    },
    onAmend: (invoice) => {
      setTarget(invoice);
      setAmendOpen(true);
    },
    onCancelInvoice: (invoice) => {
      // TODO: real cancel flow (parity with the prior list-page stub).
      console.log('Cancel invoice:', invoice.invoiceNumber);
    },
    onRecordMovement: (invoice) => {
      setDirection('collection');
      setAmountValue('');
      setCollectedDate(today());
      void openWithFreshInvoice(invoice, () => setMovementOpen(true));
    },
    onSettle: (invoice) => {
      stopCountdown();
      void openWithFreshInvoice(invoice, () => setSettleOpen(true));
    },
    onMondayOpportunityCreate: (invoice) =>
      createOpportunity.mutate(invoice.id),
  };

  const parsed =
    typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;
  // A NumberInput holding something unparseable yields NaN, and `NaN <= 0` is false - so
  // guarding on that alone leaves the button live on a figure the submit drops on the floor.
  const amountIsMoney = Number.isFinite(parsed) && parsed > 0;
  const signed = direction === 'deduction' ? -(parsed || 0) : parsed || 0;
  const resulting = (snapshot?.paidAmount ?? 0) + signed;

  const submitMovement = async () => {
    if (!target || !snapshot || !collectedDate || !amountIsMoney) return;
    setNotice(null);
    setSubmitting(true);
    try {
      const res = await recordMovement.mutateAsync({
        id: target.id.toString(),
        amount: signed,
        collectedAt: collectedDate,
        expectedPaymentSeq: snapshot.paymentSeq,
      });
      setMovementOpen(false);
      setAmountValue('');
      notifications.show({
        color: signed > 0 ? 'green' : 'orange',
        title: `${target.invoiceNumber}: ${
          signed > 0 ? 'collection' : 'deduction'
        } recorded`,
        message: `${Math.abs(signed).toFixed(2)} ${target.dueCurrency}. Collected in total: ${(
          res.invoice.paidAmount ?? 0
        ).toFixed(2)} ${target.dueCurrency}.`,
      });
    } catch (error) {
      // A 409 carries what the invoice holds now. Their figures stay in the form and are
      // never applied to the new total on their behalf - they decide again, against the truth.
      const body = error instanceof ApiError ? safeJson(error.body) : null;
      if (body?.code === 'INVOICE_CHANGED') {
        setSnapshot({
          paidAmount: body.paidAmount,
          paymentSeq: body.paymentSeq,
          payments: body.payments,
        });
        setNotice({
          title: 'This invoice was updated while you were entering',
          message:
            'Someone else recorded against it. Check the history below, then confirm again if your entry is still right.',
          tone: 'orange',
        });
      } else if (
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        setNotice({
          title: 'Not recorded',
          message: error.body || 'The entry was refused.',
          tone: 'red',
        });
      } else {
        // It may have landed. Saying it failed is how the same money gets entered twice.
        await refreshSnapshot();
        setNotice({
          title: "We didn't get a response",
          message:
            'Your entry may or may not have been recorded. Check the history below before entering it again.',
          tone: 'orange',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const refreshSnapshot = async () => {
    if (!target) return;
    try {
      const fresh = await refetchInvoiceDetails(target.id.toString());
      setTarget(fresh);
      setSnapshot(snapshotOf(fresh));
    } catch {
      /* the notice already tells them what they do not know */
    }
  };

  const dispatchSettle = async () => {
    if (!target) return;
    stopCountdown();
    setSubmitting(true);
    try {
      await settleInvoice.mutateAsync(target.id.toString());
      setSettleOpen(false);
      notifications.show({
        color: 'green',
        title: `${target.invoiceNumber} settled`,
        message: 'The client is notified and the opportunity is unlocked.',
      });
    } catch (error) {
      const refused =
        error instanceof ApiError && error.status >= 400 && error.status < 500;
      await refreshSnapshot();
      setNotice({
        title: refused ? 'Not settled' : "We didn't get a response",
        message: refused
          ? (error as ApiError).body || 'The settlement was refused.'
          : 'The invoice may or may not have been settled. Check what it shows before trying again.',
        tone: refused ? 'red' : 'orange',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const startCountdown = () => {
    setNotice(null);
    setRemaining(UNDO_SECONDS);
    timer.current = globalThis.setInterval(() => {
      setRemaining((left) => {
        if (left == null) return null;
        if (left > 1) return left - 1;
        // One guard for the expiry/Undo race: the interval is cleared here, so a click
        // landing in the same tick finds nothing left to cancel and nothing double-sent.
        stopCountdown();
        void dispatchSettle();
        return null;
      });
    }, 1000);
  };

  const closeMovement = () => {
    if (submitting) return;
    setMovementOpen(false);
  };

  const closeSettle = () => {
    if (submitting) return;
    stopCountdown();
    setSettleOpen(false);
  };

  const modals = (
    <>
      {target && (
        <PaymentConfirmationForm
          invoice={target}
          opened={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          onSubmit={async (data: PaymentConfirmationData) => {
            try {
              await paymentConfirmationMutation.mutateAsync({
                id: data.invoiceId,
                paymentMethod: data.paymentMethod,
                transactionHash: data.transactionHash,
                fileId: data.fileId,
                notes: data.notes,
              });
              setPaymentOpen(false);
            } catch (error) {
              console.error('Failed to submit payment confirmation:', error);
            }
          }}
        />
      )}

      {target && (
        <Modal
          opened={movementOpen}
          onClose={closeMovement}
          title={`Record against ${target.invoiceNumber}`}
          size="md"
          centered
          closeOnClickOutside={!submitting}
          closeOnEscape={!submitting}
          withCloseButton={!submitting}
        >
          <Stack gap="md">
            <SegmentedControl
              value={direction}
              onChange={(v) => setDirection(v as 'collection' | 'deduction')}
              disabled={submitting}
              data={[
                { value: 'collection', label: 'Money received' },
                { value: 'deduction', label: 'Money returned' },
              ]}
              fullWidth
            />

            <NumberInput
              // The figure that moved now, not what the invoice holds - the confusion behind
              // most mis-entries, so the label says so outright.
              label={
                direction === 'collection'
                  ? 'Amount received'
                  : 'Amount returned'
              }
              description="What actually moved in the account, exactly as the statement reads it."
              placeholder="Enter amount"
              value={amountValue}
              onChange={setAmountValue}
              min={0}
              step={0.01}
              decimalScale={2}
              disabled={submitting}
              required
            />

            <DateInput
              label="Actual date"
              description="The day the money moved, not today's entry"
              value={collectedDate}
              onChange={setCollectedDate}
              maxDate={new Date()}
              valueFormat="YYYY-MM-DD"
              disabled={submitting}
              required
            />

            {snapshot && amountIsMoney && (
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  Collected so far: {snapshot.paidAmount.toFixed(2)}{' '}
                  {target.dueCurrency}
                </Text>
                <Text size="sm" c="dimmed">
                  After this entry: {resulting.toFixed(2)} {target.dueCurrency}
                </Text>
              </Stack>
            )}

            {notice && (
              <Alert color={notice.tone} title={notice.title}>
                {notice.message}
              </Alert>
            )}

            <PaymentHistory
              payments={snapshot?.payments}
              currency={target.dueCurrency}
            />

            <Group justify="flex-end">
              <Button
                variant="subtle"
                onClick={closeMovement}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={submitMovement}
                loading={submitting}
                disabled={
                  submitting ||
                  !snapshot ||
                  !amountIsMoney ||
                  !collectedDate ||
                  resulting < 0
                }
              >
                {resulting < 0
                  ? 'More than has been collected'
                  : `Record ${Math.abs(signed).toFixed(2)} ${target.dueCurrency}`}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {target && (
        <Modal
          opened={settleOpen}
          onClose={closeSettle}
          title={`Settle ${target.invoiceNumber}`}
          size="md"
          centered
          closeOnClickOutside={!submitting}
          closeOnEscape={!submitting}
          withCloseButton={!submitting}
        >
          <Stack gap="md">
            {snapshot && (
              <Stack gap={2}>
                <Text size="sm">
                  Billed: {target.dueAmount?.toFixed(2)} {target.dueCurrency}
                </Text>
                <Text size="sm" fw={600}>
                  Collected: {snapshot.paidAmount.toFixed(2)}{' '}
                  {target.dueCurrency}
                </Text>
              </Stack>
            )}

            <Text size="xs" c="dimmed">
              Settling closes this invoice for what it has actually collected,
              over or under what was billed. The client is emailed and any next
              installment is released. It cannot be undone afterwards.
            </Text>

            {notice && (
              <Alert color={notice.tone} title={notice.title}>
                {notice.message}
              </Alert>
            )}

            <PaymentHistory
              payments={snapshot?.payments}
              currency={target.dueCurrency}
            />

            {remaining != null ? (
              <Group justify="space-between">
                <Text size="sm">
                  Settling in {remaining} second{remaining === 1 ? '' : 's'}
                </Text>
                <Button color="red" variant="light" onClick={stopCountdown}>
                  Undo
                </Button>
              </Group>
            ) : (
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  onClick={closeSettle}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  color="teal"
                  onClick={startCountdown}
                  loading={submitting}
                  disabled={submitting || !snapshot || snapshot.paidAmount <= 0}
                >
                  {snapshot && snapshot.paidAmount <= 0
                    ? 'Nothing collected yet'
                    : 'Settle this invoice'}
                </Button>
              </Group>
            )}
          </Stack>
        </Modal>
      )}

      {target && amendOpen && (
        <AmendInvoiceModal
          invoice={target}
          opened={amendOpen}
          onClose={() => setAmendOpen(false)}
        />
      )}
    </>
  );

  return { handlers, modals };
}

type ChangedBody = {
  code?: string;
  paidAmount: number;
  paymentSeq: number;
  payments: PaymentMovement[];
};

const safeJson = (body: string): ChangedBody | null => {
  try {
    return JSON.parse(body) as ChangedBody;
  } catch {
    return null;
  }
};
